import { buildFileUrl, getFileAsset, SanityFileSource } from "@sanity/asset-utils";
import createImageUrlBuilder from "@sanity/image-url";
import type { SanityImageSource } from "@sanity/image-url/lib/types/types";

import { SANITY_CDN_HOSTNAME } from "@/constants";
import { getLogger } from "@/lib/logger";

import { dataset, projectId } from "./api";

export type DeviceType = "mobile" | "tablet" | "desktop";

export type ResponsiveMedia = {
  image?: SanityImageSource | null;
  tablet?: SanityImageSource | null;
  mobile?: SanityImageSource | null;
  alt?: string | null;
};

const imageBuilder = createImageUrlBuilder({
  projectId: projectId || "",
  dataset: dataset || "",
});

const log = getLogger("sanity/utils");

function cacheSanityUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;

  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === SANITY_CDN_HOSTNAME) {
      const path = urlObj.pathname.substring(1);
      return `/api/sanity-assets/${path}${urlObj.search}`;
    }
    return url;
  } catch {
    return url;
  }
}

export const urlForImage = (source: SanityImageSource | null | undefined) => {
  // Ensure that source image contains a valid reference
  if (!source) {
    return undefined;
  }

  // Check if source is an object (not a string URL)
  if (typeof source === "object") {
    // Check if it's a ResponsiveMedia object with all null fields (invalid)
    if (
      "image" in source &&
      "tablet" in source &&
      "mobile" in source &&
      !source.image &&
      !source.tablet &&
      !source.mobile
    ) {
      return undefined;
    }

    // Check if it's an image reference without a valid _ref
    if ("_ref" in source && !source._ref) {
      return undefined;
    }

    // Check if it's an image object without an asset
    if ("asset" in source && !source.asset) {
      return undefined;
    }
  }

  try {
    return imageBuilder?.image(source).auto("format").fit("max");
  } catch (error) {
    log?.warn("Failed to build image URL", { source, error });
    return undefined;
  }
};

export const urlForFile = (source?: SanityFileSource) => {
  if (!source) {
    return undefined;
  }

  const fileAsset = getFileAsset(source, { projectId, dataset });

  return buildFileUrl(fileAsset, { projectId, dataset });
};

export const urlForFileCached = (source?: SanityFileSource) => {
  const url = urlForFile(source);
  return cacheSanityUrl(url);
};

export const urlForImageCached = (source: SanityImageSource | null | undefined) => {
  const builder = urlForImage(source);
  if (!builder) {
    return undefined;
  }

  const createCachedProxy = (targetBuilder: typeof builder) => {
    return new Proxy(targetBuilder, {
      get(target, prop) {
        if (prop === "url") {
          return () => {
            const url = target.url();
            return cacheSanityUrl(url) || url;
          };
        }
        if (prop === "toString") {
          return () => {
            const url = target.toString();
            return cacheSanityUrl(url) || url;
          };
        }
        const value = target[prop as keyof typeof target];
        if (typeof value === "function") {
          const boundFn = value.bind(target);
          return (...args: any[]) => {
            const result = boundFn(...args);
            if (result === target) {
              return createCachedProxy(result);
            }
            if (result && typeof result === "object" && "url" in result) {
              return createCachedProxy(result);
            }
            return result;
          };
        }
        return value;
      },
    });
  };

  return createCachedProxy(builder);
};

export function resolveOpenGraphImage(
  image: (SanityImageSource & { alt?: string | null }) | null | undefined,
  width = 1200,
  height = 627,
) {
  if (!image) return;
  const url = urlForImageCached(image)?.width(1200).height(627).fit("crop").url();
  if (!url) return;
  return { url, alt: image?.alt as string, width, height };
}

export function resolveHref(
  documentType?: string,
  slug?: string | { current?: string | null } | null,
): string | undefined {
  switch (documentType) {
    case "page":
    case "productPage": {
      const value = typeof slug === "string" ? slug : slug?.current;
      if (!value || value === "home" || value === "index") return "/";
      return value.startsWith("/") ? value : `/${value}`;
    }
    case "contentHub": {
      const value = typeof slug === "string" ? slug : slug?.current;
      if (!value) return;
      return value.startsWith("/") ? value : `/${value}`;
    }
    default:
      if (documentType) {
        log.warn("Unhandled document type in resolveHref", { documentType, slug });
      }
      return undefined;
  }
}

/**
 * Gets the best image for a specific device type with fallback logic.
 * Fallback order:
 * - Mobile: mobile → tablet → image (desktop)
 * - Tablet: tablet → image (desktop)
 * - Desktop: image (desktop) → tablet
 */
export function getImageForDevice(
  media: ResponsiveMedia | null | undefined,
  deviceType: DeviceType,
): SanityImageSource | null | undefined {
  if (!media) return null;

  switch (deviceType) {
    case "mobile":
      return media.mobile || media.tablet || media.image;
    case "tablet":
      return media.tablet || media.image;
    case "desktop":
      return media.image || media.tablet;
    default:
      return media.image;
  }
}

/**
 * Gets the responsive image source for the current device (client-side) or defaults to desktop (server-side).
 * Uses window.matchMedia to detect device type on the client.
 */
export function getResponsiveImage(
  media: ResponsiveMedia | null | undefined,
  deviceType?: DeviceType,
): SanityImageSource | null | undefined {
  if (!media) return null;

  // If deviceType is provided, use it directly
  if (deviceType) {
    return getImageForDevice(media, deviceType);
  }

  // Server-side: default to desktop (image)
  if (typeof window === "undefined") {
    return media.image || media.tablet || media.mobile;
  }

  // Client-side: detect device type
  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  const isTablet = window.matchMedia("(min-width: 641px) and (max-width: 1023px)").matches;

  if (isMobile) {
    return media.mobile || media.tablet || media.image;
  }
  if (isTablet) {
    return media.tablet || media.image;
  }
  return media.image || media.tablet;
}

/**
 * Generates a srcset string for responsive images.
 * Returns srcset with mobile, tablet, and desktop images if available.
 */
export function getImageSrcSet(
  media: ResponsiveMedia | null | undefined,
  options?: {
    mobileWidth?: number;
    tabletWidth?: number;
    desktopWidth?: number;
    useCache?: boolean;
  },
): string | undefined {
  if (!media) return undefined;

  const {
    mobileWidth = 768,
    tabletWidth = 1024,
    desktopWidth = 1920,
    useCache = true,
  } = options || {};
  const srcset: string[] = [];
  const imageBuilderFn = useCache ? urlForImageCached : urlForImage;

  if (media.mobile) {
    const url = imageBuilderFn(media.mobile)?.width(mobileWidth).url();
    if (url) srcset.push(`${url} ${mobileWidth}w`);
  }

  if (media.tablet) {
    const url = imageBuilderFn(media.tablet)?.width(tabletWidth).url();
    if (url) srcset.push(`${url} ${tabletWidth}w`);
  }

  if (media.image) {
    const url = imageBuilderFn(media.image)?.width(desktopWidth).url();
    if (url) srcset.push(`${url} ${desktopWidth}w`);
  }

  return srcset.length > 0 ? srcset.join(", ") : undefined;
}

/**
 * Resolves the preview URL for a document in Sanity Studio.
 * Returns a URL that enables draft mode and points to the document's page.
 * This function is used by the iframe-pane plugin to show previews in the Structure view.
 */
export function resolvePreviewUrl(
  document:
    | {
        _type?: string;
        slug?: { current?: string | null } | null;
        _id?: string;
      }
    | null
    | undefined,
): string | null {
  if (!document?._type) return null;

  // Get base URL for the site (not the studio)
  // In browser context (Sanity Studio), detect the site URL
  // For local dev: site runs on port 3000, studio typically on 5173
  // In production: studio is on studio.tzero.com, site is on tzero.com
  let baseUrl: string;
  if (typeof window !== "undefined") {
    // Browser context - Sanity Studio
    // Check for environment variable first
    // Support both Next.js and Vite environment variables
    let siteUrl: string | undefined;
    try {
      // Try Vite's import.meta.env (for studio app)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - import.meta is available in Vite but not in Next.js
      if (typeof import.meta !== "undefined" && import.meta.env) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        siteUrl = import.meta.env.VITE_SITE_URL;
      }
    } catch {
      // import.meta not available (Next.js context)
    }
    // Fallback to process.env (works in both contexts)
    siteUrl = siteUrl || process.env.NEXT_PUBLIC_SITE_URL || process.env.VITE_SITE_URL;

    if (siteUrl) {
      baseUrl = siteUrl;
    } else {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;

      // For localhost, assume site is on port 3000
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        baseUrl = `${protocol}//${hostname}:3000`;
      } else if (hostname.startsWith("studio.")) {
        // In production, studio is on studio.tzero.com, site is on tzero.com
        baseUrl = `${protocol}//${hostname.replace("studio.", "")}`;
      } else {
        // Fallback to origin (for Next.js app context)
        baseUrl = window.location.origin;
      }
    }
  } else {
    // Server context fallback (shouldn't happen in iframe pane, but safe fallback)
    baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
  }

  let path: string | undefined;

  // Handle article types (news, media, insights, podcast, learn)
  const articleTypes = ["news", "media", "insights", "podcast", "learn"];
  if (articleTypes.includes(document._type)) {
    const slug = typeof document.slug === "string" ? document.slug : document.slug?.current;
    if (!slug) return null;
    path = `/${document._type}/${slug}`;
  }
  // Handle pages and product pages
  else if (document._type === "page" || document._type === "productPage") {
    const slug = typeof document.slug === "string" ? document.slug : document.slug?.current;
    if (!slug || slug === "home" || slug === "index") {
      path = "/";
    } else {
      path = slug.startsWith("/") ? slug : `/${slug}`;
    }
  }
  // Handle content hubs
  else if (document._type === "contentHub") {
    const slug = typeof document.slug === "string" ? document.slug : document.slug?.current;
    if (!slug) return null;
    path = slug.startsWith("/") ? slug : `/${slug}`;
  }
  // Unknown document type - don't show preview
  else {
    return null;
  }

  // Enable draft mode and redirect to the page
  return `${baseUrl}/api/draft-mode/enable?redirect=${encodeURIComponent(path)}`;
}
